import { Injectable } from '@angular/core';
import {
    ClearNotificationBatchRequest,
    NotificationControllerService,
    NotificationResponse,
    PagedNotificationSearchRequest,
    SortablePagedRequestNotificationSortBy,
} from '@core/generated/openapi';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { HttpCache, HttpCacheService } from '@rcp/common-web';
import {
    CACHE_KEYS,
    CACHE_TIMEOUT_FIVEMINUTES,
} from '@core/constants/cache-keys.constants';
import { map } from 'rxjs/operators';
import { isNil } from 'lodash-es';
import dayjs from 'dayjs';
import {
    NotificationFilter,
    NotificationOrderOptions,
    NotificationSortOptions,
} from '@core/constants/notification.constants';
import { GENERAL_STATE_KEYS } from '@core/constants/general-store.constants';
import { GeneralStateService } from '@services/state/general-state.service';
import { ConfirmationDialogComponent } from '@modules/common/modules/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogData } from '@core/constants/confirmation-dialog.constants';
import { MatDialog } from '@angular/material/dialog';
import { NotificationServiceUP } from '@services/notifications/notification-service-u-p.service';
import { ReplaySubject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class UserNotificationsService {
    constructor(
        private service: NotificationControllerService,
        private formBuilder: FormBuilder,
        private httpCacheService: HttpCacheService,
        private generalStateService: GeneralStateService,
        private dialog: MatDialog,
        private notificationServiceUP: NotificationServiceUP,
    ) {}

    clearNotification(formGroup: FormGroup) {
        let request = {
            id: formGroup.controls['id'].value,
            message: formGroup.controls['message'].value,
        };
        return this.service.clearNotification(request);
    }

    clearAllNotifications(notifications: NotificationResponse[]) {
        let result$ = new ReplaySubject<boolean>(1);

        if (isNil(notifications)) {
            result$.next(false);
        }

        this.dialog
            .open(ConfirmationDialogComponent, {
                data: <ConfirmationDialogData>{
                    title: 'Clear All Notifications?',
                    message: `Are you sure you want to clear ${notifications?.length} notifications?`,
                    confirmText: 'Yes',
                    cancelText: 'No',
                },
                width: '425px',
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    const notificationIds: number[] = notifications
                        .filter(
                            (n) =>
                                n.status ===
                                NotificationResponse.StatusEnum.Active,
                        )
                        .map((n) => n.id);

                    const request: ClearNotificationBatchRequest = {
                        notificationIds: notificationIds,
                    };

                    this.service.clearNotificationBatch(request).subscribe({
                        next: () => {
                            this.notificationServiceUP.showSuccessMessage(
                                'All Notifications Cleared.',
                                '',
                            );
                            this.bustAllNotificationDataCache();
                            result$.next(true);
                        },
                        error: (error) => {
                            this.notificationServiceUP.showError(
                                'Error updating notifications.',
                                error.error,
                            );
                            console.error(error);
                        },
                    });
                } else {
                    result$.next(false);
                }
            });

        return result$.asObservable();
    }

    getEditNotificationForm(record: NotificationResponse) {
        let formGroup = this.formBuilder.group({
            id: new FormControl({ value: record.id, disabled: true }),
            description: new FormControl(
                {
                    value: record.description,
                    disabled: true,
                },
                [Validators.maxLength(2000)],
            ),
            notificationPriority: new FormControl({
                value: record.notificationPriority,
                disabled: true,
            }),
            message: new FormControl('', [Validators.maxLength(250)]),
        });
        return formGroup;
    }

    findNotificationById(id: number) {
        return this.service.findNotificationById(id);
    }

    getAllOpenNotifications(bustCache: boolean) {
        if (bustCache) {
            this.bustAllNotificationDataCache();
            return this._getAllOpenNotifications();
        } else {
            return this._getAllOpenNotifications();
        }
    }

    @HttpCache(
        CACHE_KEYS.USER_NOTIFICATIONS.getAllOpenNotifications,
        CACHE_TIMEOUT_FIVEMINUTES,
    )
    private _getAllOpenNotifications() {
        let request: PagedNotificationSearchRequest = {
            statusList: [PagedNotificationSearchRequest.StatusListEnum.Active],
            createdOnRange: null,
            paged: { unPaged: true },
        };
        return this.service
            .findAllNotificationsByPagedGeneralSearch(request)
            .pipe(
                map((response) => ({
                    ...response,
                    items: response.items.sort(
                        (a, b) =>
                            new Date(b.createdOn).getTime() -
                            new Date(a.createdOn).getTime(),
                    ),
                })),
            );
    }

    getAllClearedNotifications(paged?: SortablePagedRequestNotificationSortBy) {
        let request: PagedNotificationSearchRequest = {
            statusList: [
                PagedNotificationSearchRequest.StatusListEnum.Inactive,
            ],
            createdOnRange: null,
            paged: paged ? paged : { unPaged: true },
        };
        return this.service
            .findAllNotificationsByPagedGeneralSearch(request)
            .pipe(
                map((response) => ({
                    ...response,
                    items: response.items.sort(
                        (a, b) =>
                            new Date(b.createdOn).getTime() -
                            new Date(a.createdOn).getTime(),
                    ),
                })),
            );
    }

    /**
     * This will bust all caches for notification data. To be used when notification actions are taken.
     */
    bustAllNotificationDataCache() {
        this.httpCacheService.invalidateTag(
            CACHE_KEYS.USER_NOTIFICATIONS.getAllOpenNotifications,
        );
    }

    sortByCreationDate(
        notifications: NotificationResponse[],
        order: NotificationOrderOptions,
    ): NotificationResponse[] {
        return !isNil(notifications)
            ? notifications.sort(
                  (a: NotificationResponse, b: NotificationResponse) => {
                      if (order === NotificationOrderOptions.ASC) {
                          return dayjs(a.createdOn).diff(dayjs(b.createdOn));
                      } else {
                          return dayjs(b.createdOn).diff(dayjs(a.createdOn));
                      }
                  },
              )
            : notifications;
    }

    sortByClearedDate(
        notifications: NotificationResponse[],
        order: NotificationOrderOptions,
    ): NotificationResponse[] {
        return !isNil(notifications)
            ? notifications.sort(
                  (a: NotificationResponse, b: NotificationResponse) => {
                      if (order === NotificationOrderOptions.ASC) {
                          return dayjs(a.clearedDate).diff(
                              dayjs(b.clearedDate),
                          );
                      } else {
                          return dayjs(b.clearedDate).diff(
                              dayjs(a.clearedDate),
                          );
                      }
                  },
              )
            : notifications;
    }

    getSortFieldName(
        filter: NotificationFilter,
    ): SortablePagedRequestNotificationSortBy.SortFieldNameEnum {
        if (isNil(filter)) {
            return SortablePagedRequestNotificationSortBy.SortFieldNameEnum
                .CreatedOn;
        }

        switch (filter?.sort) {
            case NotificationSortOptions.CLEARED_DATE:
                return SortablePagedRequestNotificationSortBy.SortFieldNameEnum
                    .ClearedDate;
            case NotificationSortOptions.CREATION_DATE:
                return SortablePagedRequestNotificationSortBy.SortFieldNameEnum
                    .CreatedOn;
            default:
                return SortablePagedRequestNotificationSortBy.SortFieldNameEnum
                    .CreatedOn;
        }
    }

    getSortDirection(
        filter: NotificationFilter,
    ): SortablePagedRequestNotificationSortBy.SortDirectionEnum {
        if (isNil(filter)) {
            return SortablePagedRequestNotificationSortBy.SortDirectionEnum
                .Desc;
        }

        return filter.order === NotificationOrderOptions.ASC
            ? SortablePagedRequestNotificationSortBy.SortDirectionEnum.Asc
            : SortablePagedRequestNotificationSortBy.SortDirectionEnum.Desc;
    }

    saveFilterState(key: string, filter: NotificationFilter) {
        this.generalStateService.setGeneralState(key, filter);
    }

    getFilterState(key: string): NotificationFilter {
        return this.generalStateService.getGeneralState(key);
    }

    getFilterStateKey(view: string): string {
        return GENERAL_STATE_KEYS.NOTIFICATIONS_FILTER + view;
    }
}
